import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useGlobalStateStore } from './globalState'
import type { Chat } from '@/types'
import { useUserStore } from '@/store/user'
import { userService } from '@/services/user'
import type { EventTrackRequest } from '@/types/eventTrack'
import { baseURL } from '@/services/base'

export const useChatsStore = defineStore('chats', () => {
  const userStore = useUserStore()
  const globalStore = useGlobalStateStore()
  const chats = ref<Chat[]>([])

  function addChats(newChats: Chat[]) {
    chats.value.push(...newChats)
  }

  // The message input when user is logged out, keep the input
  const logoutChatInput = ref<string>('')
  const previewChatId = ref<string>()

  function setPreviewChatId(chatId: string) {
    previewChatId.value = chatId
  }

  const currentChatIndex = ref<number>(0)
  const currentChatId = ref<string>()

  const hasWatchedSekaiMap = ref<Map<number, boolean>>(new Map())
  /**
   * Report the user has watched the current sekai to backend
   * @param chat Chat
   */
  async function reportWatchedUniverse(chat: Chat) {
    if (hasWatchedSekaiMap.value.has(chat.universe.universeId)) {
      return
    }

    if (userStore.isLoggedIn) {
      try {
        await userService.reportWatchedUniverse({
          universeId: chat.universe.universeId,
        })

        // Update the hasWatchedSekaiMap
        hasWatchedSekaiMap.value.set(chat.universe.universeId, true)
      }
      catch (err) {
        console.error(err)
      }
    }
  }

  function setCurrentChatIndex(index: number) {
    currentChatIndex.value = index
  }

  function setCurrentChatId(chatId: string) {
    currentChatId.value = chatId
  }

  function setNewCreatedChat(chat: Chat) {
    // put the new chat to the chat list
    // after the currentChatIndex chat
    // generate new chatId to seperate the chat

    chats.value.splice(currentChatIndex.value + 1, 0, chat)
    setCurrentChatId(chat.chatId)
    setCurrentChatIndex(currentChatIndex.value + 1)

    // Swipe the outside swiper to current chat
    const swiperEl = document.querySelector('swiper-container')
    if (swiperEl) {
      swiperEl.swiper.slideNext()
    }

    return chat
  }

  const loadingChattedSekai = ref<boolean>(false)
  const errorChattedSekai = ref<string>('')
  /**
   * 埋点
   * @param sekaiId 宇宙ID
   * @param nodeSessionId ChatId
   * @param nodeType message_visit | scenario_visit
   * @param index message_index | scenario_index
   * @returns EventTrackRequest
   */
  function eventPayload(
    sekaiId: string,
    nodeSessionId: string,
    nodeType: string,
    index: number,
  ): EventTrackRequest {
    const req = {
      user_id: `${userStore.user.userId}`,
      device_id: `${globalStore.deviceId!.identifier}`,
      app_version: `1.4.7-${baseURL.includes('stage') ? 'stage' : 'prod'}`,
      os_version: `${globalStore.deviceInfo?.platform}-${globalStore.deviceInfo!.osVersion}`,
      event_list: [
        {
          event_type: 'sekai_visit_node',
          event_time: new Date().toISOString(),
          event_data: {
            sekai_id: sekaiId,
            node_session_id: nodeSessionId,
            node_type: nodeType,
            node_data: {},
          },
        },
      ],
    }

    if (nodeType === 'scenario_visit') {
      req.event_list[0].event_data.node_data['scenario_index'] = index
    }
    else if (nodeType === 'message_visit') {
      req.event_list[0].event_data.node_data['message_index'] = index
    }

    return req
  }

  return {
    chats,
    addChats,

    logoutChatInput,
    setPreviewChatId,
    reportWatchedUniverse,
    currentChatId,
    currentChatIndex,
    setCurrentChatIndex,
    setCurrentChatId,

    setNewCreatedChat,

    loadingChattedSekai,
    errorChattedSekai,

    eventPayload,
  }
})
